/**
 * TaskDetailModal - displays full task details in a centered modal with editable fields
 */

import { useEffect, useState, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  X,
  Calendar,
  CalendarPlus,
  Clock,
  Tag,
  CheckSquare,
  Trash2,
  ChevronDown,
  Mic,
  Mail,
  Link2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from '@/i18n/dateFns';
import { ConfirmDialog } from '@components/ui/ConfirmDialog';
import { useTaskStore } from '@stores/taskStore';
import { useBoardStore } from '@stores/boardStore';
import { useAuthStore } from '@stores/authStore';
import { projectsApi, type ProjectMember } from '@services/api';
import Badge from '@components/ui/Badge';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import SubtaskList from './SubtaskList';
import LabelPicker from './LabelPicker';
import TaskComments from './TaskComments';
import TaskAttachments from './TaskAttachments';
import TaskCustomFields from './TaskCustomFields';
import { AssigneePicker, type AssigneeOption } from './AssigneePicker';
import { toast } from '@components/ui/Toast';

interface TaskDetailModalProps {
  taskId: string | null;
  open: boolean;
  onClose: () => void;
  onDelete?: (taskId: string) => void;
}

type PriorityValue = 'urgent' | 'high' | 'medium' | 'low' | 'none';

const priorityOptions: { value: PriorityValue; label: string; color: string; bg: string }[] = [
  { value: 'urgent', label: 'Urgent', color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' },
  { value: 'high', label: 'High', color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
  { value: 'low', label: 'Low', color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  { value: 'none', label: 'None', color: 'text-neutral-400', bg: 'bg-neutral-100 dark:bg-neutral-800' },
];

export default function TaskDetailModal({
  taskId,
  open,
  onClose,
  onDelete,
}: TaskDetailModalProps) {
  const { t } = useTranslation();
  const {
    currentTask,
    subtasks,
    labels: availableLabels,
    isLoading,
    isSaving,
    error,
    fetchTask,
    updateTask,
    toggleSubtask,
    createSubtask,
    deleteSubtask,
    completeTask,
    uncompleteTask,
    fetchLabels,
    createLabel,
    addLabelToTask,
    removeLabelFromTask,
    reset,
  } = useTaskStore();

  const { currentBoard } = useBoardStore();
  const { currentOrgId } = useAuthStore();

  const [discardOpen, setDiscardOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');

  // Dropdown states
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);
  const [dueDateValue, setDueDateValue] = useState('');
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);

  const priorityRef = useRef<HTMLDivElement>(null);
  const dueDateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (taskId && open) {
      void fetchTask(taskId).catch(() => undefined);
    } else {
      reset();
    }
    setDiscardOpen(false);
    setEditError(null);
    setEditingTitle(false);
    setEditingDescription(false);
    setShowPriorityDropdown(false);
    setShowDueDatePicker(false);
    return () => reset();
  }, [taskId, open, fetchTask, reset]);

  useEffect(() => {
    if (currentTask?.id === taskId) {
      if (!editingTitle) setTitleValue(currentTask.title);
      if (!editingDescription) setDescriptionValue(currentTask.description || '');
      const dateStr = currentTask.dueDate?.split('T')[0];
      setDueDateValue(dateStr ?? '');
    }
  }, [currentTask, taskId, editingTitle, editingDescription]);

  // Fetch project members and labels when board is available
  useEffect(() => {
    if (currentBoard?.projectId && open) {
      projectsApi.listMembers(currentBoard.projectId)
        .then(members => setProjectMembers(members))
        .catch(err => console.error('Failed to load project members:', err));
      fetchLabels(currentBoard.projectId).catch(err =>
        console.error('Failed to load labels:', err)
      );
    }
  }, [currentBoard?.projectId, open, fetchLabels]);

  // Close dropdowns when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (priorityRef.current && !priorityRef.current.contains(event.target as Node)) {
        setShowPriorityDropdown(false);
      }
      if (dueDateRef.current && !dueDateRef.current.contains(event.target as Node)) {
        setShowDueDatePicker(false);
      }
    };

    // Close an open dropdown on Escape without letting the modal close too
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showPriorityDropdown || showDueDatePicker) {
        event.preventDefault();
        event.stopPropagation();
        setShowPriorityDropdown(false);
        setShowDueDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [showPriorityDropdown, showDueDatePicker]);

  const handleTitleSave = async () => {
    if (isSaving) return;
    setEditError(null);
    if (!titleValue.trim()) {
      setEditError(t('task.titleRequired', 'Enter a title.'));
      return;
    }
    if (currentTask && titleValue.trim() !== currentTask.title) {
      try {
        await updateTask(currentTask.id, { title: titleValue.trim() });
      } catch {
        setEditError(t('task.editSaveFailed', 'Could not save. Your changes are still here.'));
        return;
      }
    }
    setEditingTitle(false);
  };

  const handleDescriptionSave = async () => {
    if (isSaving) return;
    setEditError(null);
    if (currentTask) {
      const newDesc = descriptionValue.trim();
      if (newDesc !== (currentTask.description || '')) {
        try {
          await updateTask(currentTask.id, { description: newDesc });
        } catch {
          setEditError(t('task.editSaveFailed', 'Could not save. Your changes are still here.'));
          return;
        }
      }
    }
    setEditingDescription(false);
  };

  const handleToggleComplete = async () => {
    if (!currentTask) return;
    try {
      if (currentTask.isCompleted) {
        await uncompleteTask(currentTask.id);
        toast.success(t('task.toastMarkedIncomplete'));
      } else {
        await completeTask(currentTask.id);
        toast.success(t('task.toastCompleted'));
      }
    } catch {
      toast.error(t('errors.generic', 'Failed to update task status'));
    }
  };

  const handlePriorityChange = async (priority: PriorityValue) => {
    if (!currentTask) return;
    try {
      await updateTask(currentTask.id, { priority });
      toast.success(t('task.toastPriorityUpdated'));
    } catch {
      toast.error(t('errors.generic', 'Failed to update priority'));
    }
    setShowPriorityDropdown(false);
  };

  const handleDueDateChange = async (date: string) => {
    if (!currentTask) return;
    try {
      await updateTask(currentTask.id, { dueDate: date || null });
      toast.success(date ? t('task.toastDueDateUpdated') : t('task.toastDueDateRemoved'));
    } catch {
      toast.error(t('errors.generic', 'Failed to update due date'));
    }
    setShowDueDatePicker(false);
  };

  const handleAssigneeChange = async (assignee: AssigneeOption | null) => {
    if (!currentTask) return;
    try {
      if (assignee === null) {
        // Unassign
        await updateTask(currentTask.id, { assigneeId: null, assigneeContactId: null });
        toast.success(t('task.toastAssigneeRemoved'));
      } else if (assignee.type === 'user') {
        // Assign to platform user
        await updateTask(currentTask.id, { assigneeId: assignee.id, assigneeContactId: null });
        toast.success(t('task.toastAssigneeUpdated'));
      } else {
        // Assign to contact
        await updateTask(currentTask.id, { assigneeId: null, assigneeContactId: assignee.id });
        toast.success(t('task.toastAssigneeUpdated'));
      }
    } catch {
      toast.error(t('errors.generic', 'Failed to update assignee'));
    }
  };

  const handleAddLabel = async (labelId: string) => {
    if (!currentTask) return;
    try {
      await addLabelToTask(currentTask.id, labelId);
      toast.success(t('task.toastLabelAdded'));
    } catch {
      toast.error(t('task.failedCreateLabel'));
    }
  };

  const handleRemoveLabel = async (labelId: string) => {
    if (!currentTask) return;
    try {
      await removeLabelFromTask(currentTask.id, labelId);
      toast.success(t('task.toastLabelRemoved'));
    } catch {
      toast.error(t('task.failedDeleteLabel'));
    }
  };

  const handleCreateLabel = async (data: { name: string; color: string }) => {
    if (!currentBoard?.projectId) throw new Error('No project selected');
    return createLabel(currentBoard.projectId, data);
  };

  const hasUnsavedEdits = (editingTitle && titleValue.trim() !== currentTask?.title)
    || (editingDescription && descriptionValue.trim() !== (currentTask?.description || ''));
  const requestClose = () => {
    if (isSaving) return;
    if (hasUnsavedEdits) setDiscardOpen(true);
    else onClose();
  };
  const editActions = (save: () => Promise<void>, cancel: () => void) => (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <button disabled={isSaving} onClick={() => void save()} className="rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:opacity-50">
        {isSaving ? t('common.saving') : t('common.save')}
      </button>
      <button disabled={isSaving} onClick={() => { setEditError(null); cancel(); }} className="rounded-lg px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800">{t('common.cancel')}</button>
      {editError && <p role="alert" className="w-full text-sm text-red-600 dark:text-red-400">{editError}</p>}
    </div>
  );

  if (!open) return null;

  const defaultPriority = priorityOptions[4]!; // 'none' is always the last option
  const currentPriority = priorityOptions.find(p => p.value === currentTask?.priority) ?? defaultPriority;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && requestClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={clsx(
            'fixed inset-0 z-[70] bg-neutral-950/45',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <Dialog.Content
          className={clsx(
            'task-detail-surface fixed left-1/2 top-1/2 z-[71] w-[calc(100%-1rem)] max-w-5xl -translate-x-1/2 -translate-y-1/2',
            'max-h-[calc(100dvh-2rem)] overflow-hidden rounded-xl bg-white shadow-xl dark:bg-neutral-900',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'flex flex-col'
          )}
          onEscapeKeyDown={(event) => { if (isSaving) event.preventDefault(); }}
          onPointerDownOutside={(event) => { if (isSaving) event.preventDefault(); }}
          aria-describedby={undefined}
        >
          <VisuallyHidden.Root>
            <Dialog.Title>{t('task.detailsTitle')}</Dialog.Title>
          </VisuallyHidden.Root>
          <Dialog.Close asChild>
            <button className="absolute right-3 top-3 z-10 rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-neutral-800"
              aria-label={t('common.close')}>
              <X className="h-5 w-5" />
            </button>
          </Dialog.Close>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <LoadingSpinner size="lg" className="text-primary-600" />
            </div>
          ) : currentTask?.id === taskId ? (
            <div className="flex max-h-[calc(100dvh-2rem)] flex-col">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-neutral-200 pl-6 pr-14 py-4 dark:border-neutral-800">
                <div className="flex items-center gap-3">
                  <span className="shrink-0 whitespace-nowrap text-sm font-medium text-neutral-500 dark:text-neutral-400">
                    {currentTask.taskKey}
                  </span>
                  <button
                    onClick={handleToggleComplete}
                    disabled={isSaving}
                    className={clsx(
                      'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                      currentTask.isCompleted
                        ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700',
                      isSaving && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <CheckSquare className="h-3.5 w-3.5" />
                    {currentTask.isCompleted ? t('task.completed') : t('task.markComplete')}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard
                        .writeText(window.location.href)
                        .then(() => toast.success(t('task.linkCopied')))
                        .catch(() => toast.error(t('errors.generic')));
                    }}
                    className="rounded p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    title={t('task.copyLink')}
                    aria-label={t('task.copyLink')}
                  >
                    <Link2 className="h-4 w-4" />
                  </button>
                  {onDelete && (
                    <button
                      onClick={() => onDelete(currentTask.id)}
                      className="rounded p-2 text-neutral-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30"
                      title={t('task.deleteTaskTooltip')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}

                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-5 sm:p-7">
                  {/* Title */}
                  {editingTitle ? (
                    <>
                    <input
                      type="text"
                      aria-label={t('task.editTitle', 'Edit title')}
                      value={titleValue}
                      onChange={(e) => setTitleValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleTitleSave();

                      }}
                      className={clsx(
                        'w-full rounded border-0 bg-transparent font-sans text-2xl font-semibold leading-snug',
                        'text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500',
                        'dark:text-white'
                      )}
                      autoFocus
                    />
                    {editActions(handleTitleSave, () => { setTitleValue(currentTask?.title || ''); setEditingTitle(false); })}
                    </>
                  ) : (
                    <h2
                      role="button"
                      tabIndex={0}
                      aria-label={t('task.editTitle', 'Edit title')}
                      onClick={() => setEditingTitle(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setEditingTitle(true);
                        }
                      }}
                      className={clsx(
                        'cursor-pointer font-sans text-2xl font-semibold leading-snug text-neutral-900 dark:text-white',
                        'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                        'focus:outline-none focus:ring-2 focus:ring-primary-500',
                        'rounded px-1 -mx-1 py-0.5',
                        currentTask.isCompleted && 'line-through opacity-60'
                      )}
                    >
                      {currentTask.title}
                    </h2>
                  )}

                  <div className="mt-6 grid gap-7 md:grid-cols-[minmax(0,1fr)_240px]">
                  <div className="order-2 min-w-0 md:order-1">
                  {/* Description */}
                  <div className="">
                    <h3 className="mb-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      {t('task.description')}
                    </h3>
                    {editingDescription ? (
                      <>
                      <textarea
                        aria-label={t('task.editDescription', 'Edit description')}
                        value={descriptionValue}
                        onChange={(e) => setDescriptionValue(e.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                            event.preventDefault(); void handleDescriptionSave();
                          }
                        }}
                        className={clsx(
                          'min-h-[160px] w-full rounded-lg border border-neutral-300 p-3',
                          'text-sm text-neutral-900 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white',
                          'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                        )}
                        placeholder={t('task.descriptionPlaceholder')}
                        autoFocus
                      />
                      {editActions(handleDescriptionSave, () => { setDescriptionValue(currentTask?.description || ''); setEditingDescription(false); })}
                      </>
                    ) : (
                      <div
                        role="button"
                        tabIndex={0}
                        aria-label={t('task.editDescription', 'Edit description')}
                        onClick={() => setEditingDescription(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setEditingDescription(true);
                          }
                        }}
                        className={clsx(
                          'min-h-12 cursor-pointer whitespace-pre-wrap break-words rounded-lg border border-transparent px-3 py-2',
                          'text-sm text-neutral-700 dark:text-neutral-300',
                          'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                          'focus:outline-none focus:ring-2 focus:ring-primary-500',
                          !currentTask.description && 'text-neutral-400'
                        )}
                      >
                        {currentTask.description || t('task.clickToAddDescription')}
                      </div>
                    )}
                  </div>


                  {/* Subtasks */}
                  <div className="mt-5">
                    <SubtaskList
                      taskId={currentTask.id}
                      subtasks={subtasks}
                      onToggle={toggleSubtask}
                      onCreate={createSubtask}
                      onDelete={deleteSubtask}
                    />
                  </div>

                  {/* Attachments */}
                  <div className="mt-5">
                    <TaskAttachments key={currentTask.id} taskId={currentTask.id} />
                  </div>

                  {/* Comments */}
                  <div className="mt-5">
                    <TaskComments key={currentTask.id} taskId={currentTask.id} />
                  </div>
                  </div>
                  <aside aria-label={t('task.properties', 'Properties')} className="order-1 min-w-0 rounded-xl border border-neutral-200 bg-neutral-50/70 p-4 dark:border-neutral-800 dark:bg-neutral-950/40 md:order-2 md:self-start">
                    <h3 className="mb-3 font-sans text-xs font-semibold uppercase tracking-wider text-neutral-500">{t('task.properties', 'Properties')}</h3>
                  {/* Properties row */}
                  <div className="task-properties grid grid-cols-2 items-start gap-2 md:flex md:flex-col md:gap-3">
                    {/* Assignee */}
                    <AssigneePicker
                      organizationId={currentOrgId ?? ''}
                      projectMembers={projectMembers}
                      currentAssignee={
                        currentTask.assignee
                          ? {
                              type: currentTask.assignee.assigneeType === 'contact' ? 'contact' : 'user',
                              id: currentTask.assignee.id,
                              displayName: currentTask.assignee.displayName,
                              avatarUrl: currentTask.assignee.avatarUrl,
                              email: currentTask.assignee.email,
                              company: currentTask.assignee.company,
                            }
                          : null
                      }
                      onSelect={handleAssigneeChange}
                      disabled={isSaving}
                    />

                    {/* Due date */}
                    <div ref={dueDateRef} className="relative">
                      <button
                        onClick={() => setShowDueDatePicker(!showDueDatePicker)}
                        aria-haspopup="dialog"
                        aria-expanded={showDueDatePicker}
                        className={clsx(
                          'flex min-h-10 items-center gap-2 rounded-lg px-2 py-2',
                          'hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors',
                          'text-sm'
                        )}
                      >
                        <Calendar className="h-4 w-4 text-neutral-400" />
                        <span className={currentTask.dueDate ? 'text-neutral-900 dark:text-white' : 'text-neutral-500 dark:text-neutral-400'}>
                          {currentTask.dueDate
                            ? format(new Date(currentTask.dueDate), 'PP')
                            : t('task.noDueDate')}
                        </span>
                        <ChevronDown className="h-3 w-3 text-neutral-400" />
                      </button>

                      {showDueDatePicker && (
                        <div className="absolute left-0 top-full z-10 mt-1 w-48 rounded-lg border border-neutral-200 bg-white p-3 shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
                          <input
                            type="date"
                            value={dueDateValue}
                            onChange={(e) => {
                              setDueDateValue(e.target.value);
                              handleDueDateChange(e.target.value);
                            }}
                            className={clsx(
                              'w-full rounded border border-neutral-300 px-2 py-1 text-sm',
                              'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white',
                              'focus:border-primary-500 focus:outline-none'
                            )}
                          />
                          {currentTask.dueDate && (
                            <button
                              onClick={() => {
                                setDueDateValue('');
                                handleDueDateChange('');
                              }}
                              className="mt-2 w-full rounded bg-neutral-100 px-2 py-1 text-xs text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-600"
                            >
                              {t('task.clearDueDate')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Priority */}
                    <div ref={priorityRef} className="relative">
                      <button
                        onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                        aria-haspopup="menu"
                        aria-expanded={showPriorityDropdown}
                        className={clsx(
                          'flex min-h-10 items-center gap-2 rounded-lg px-2 py-2',
                          'hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors',
                          'text-sm'
                        )}
                      >
                        <span className={clsx('h-2 w-2 rounded-full', currentPriority.bg)} />
                        <span className={currentPriority.color}>{t(`task.priorities.${currentPriority.value}`)}</span>
                        <ChevronDown className="h-3 w-3 text-neutral-400" />
                      </button>

                      {showPriorityDropdown && (
                        <div className="absolute left-0 top-full z-10 mt-1 w-36 rounded-lg border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
                          {priorityOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => handlePriorityChange(option.value)}
                              className={clsx(
                                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                                'hover:bg-neutral-100 dark:hover:bg-neutral-700',
                                currentTask.priority === option.value && 'bg-neutral-100 dark:bg-neutral-700'
                              )}
                            >
                              <span className={clsx('h-2 w-2 rounded-full', option.bg)} />
                              <span className={option.color}>{t(`task.priorities.${option.value}`)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Time tracking */}
                    {currentTask.estimatedMinutes && (
                      <div className="flex items-center gap-2 px-3 py-1.5 text-sm">
                        <Clock className="h-4 w-4 text-neutral-400" />
                        <span className="text-neutral-900 dark:text-white">
                          {formatMinutes(currentTask.actualMinutes || 0)} / {formatMinutes(currentTask.estimatedMinutes)}
                        </span>
                      </div>
                    )}

                    {/* Creation date */}
                    <div className="flex items-center gap-2 px-3 py-1.5 text-sm">
                      <CalendarPlus className="h-4 w-4 text-neutral-400" />
                      <span className="text-neutral-500 dark:text-neutral-400">
                        {t('task.created', { date: format(new Date(currentTask.createdAt), 'PP') })}
                      </span>
                    </div>

                    {/* Source meeting link */}
                    {currentTask.sourceMeeting && (
                      <Link
                        to={`/meetings/${currentTask.sourceMeetingId}`}
                        className={clsx(
                          'flex min-h-10 items-center gap-2 rounded-lg px-2 py-2',
                          'hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors',
                          'text-sm text-primary-600 dark:text-primary-400'
                        )}
                        title={t('task.viewSourceMeeting')}
                        onClick={() => onClose()}
                      >
                        <Mic className="h-4 w-4" />
                        <span className="max-w-[150px] truncate">
                          {currentTask.sourceMeeting.title}
                        </span>
                      </Link>
                    )}

                    {/* Source email link */}
                    {currentTask.sourceEmail && (
                      <Link
                        to={`/emails/${currentTask.sourceEmailId}`}
                        className={clsx(
                          'flex min-h-10 items-center gap-2 rounded-lg px-2 py-2',
                          'hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors',
                          'text-sm text-blue-600 dark:text-blue-400'
                        )}
                        title={t('task.viewSourceEmail')}
                        onClick={() => onClose()}
                      >
                        <Mail className="h-4 w-4" />
                        <span className="max-w-[150px] truncate">
                          {currentTask.sourceEmail.subject}
                        </span>
                      </Link>
                    )}
                  </div>

                  {/* Labels */}
                  <div className="mt-5">
                    <h3 className="mb-2 flex items-center gap-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      <Tag className="h-4 w-4" />
                      {t('task.labels')}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2">
                      {currentTask.labels?.map((label) => (
                        <Badge
                          key={label.id}
                          color={label.color}
                          onRemove={() => handleRemoveLabel(label.id)}
                        >
                          {label.name}
                        </Badge>
                      ))}
                      <LabelPicker
                        selectedLabels={currentTask.labels || []}
                        availableLabels={availableLabels}
                        onAddLabel={handleAddLabel}
                        onRemoveLabel={handleRemoveLabel}
                        onCreateLabel={handleCreateLabel}
                        compact={currentTask.labels && currentTask.labels.length > 0}
                      />
                    </div>
                  </div>

                  <TaskCustomFields key={`fields-${currentTask.id}`} taskId={currentTask.id} members={projectMembers} />
                  </aside>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-64 flex-col items-center justify-center gap-4 text-neutral-500">
              <p role="alert">{error || t('task.taskNotFound')}</p>
              <button onClick={() => taskId && void fetchTask(taskId).catch(() => undefined)}
                className="rounded-lg border border-neutral-300 px-4 py-2 text-sm dark:border-neutral-700">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
      <ConfirmDialog open={discardOpen} onClose={() => setDiscardOpen(false)}
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        title={t('task.unsavedChanges', 'Unsaved changes')}
        description={t('task.discardDescription', 'Your title or description has not been saved.')}
        confirmLabel={t('task.discardChanges', 'Discard changes')}
        cancelLabel={t('task.keepEditing', 'Keep editing')} />
    </Dialog.Root>
  );
}

function formatMinutes(minutes: number): string {
  if (minutes === 0) return '0m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}
